import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../util/async/apiUtils";
import { useSocket } from "../components/socket/SocketProvider";
import { getProfileDetails } from "../service/authService";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { getDeviceId } from "../util/deviceIdentity";

/**
 * Shell for /chats/:id and /text-chat/:id.
 * Must NOT kick consultants/clients home on chat-only sessions (no OpenTok sessionId).
 * 2nd+ Call Now chat was failing because missing sessionId triggered navigate("/").
 */
const BlankLayout = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const socket = useSocket();
  const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
  const [appointmentData, setAppointmentData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("appointmentData") || "null");
    } catch {
      return null;
    }
  });
  const [profile, setProfile] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "null");
    } catch {
      return null;
    }
  });
  const [isAuth, setIsAuth] = useState(false);

  const urlAppointmentId = id ? String(id) : "";

  const navigateHome = useCallback(
    (reason) => {
      console.warn("[BlankLayout] leave call shell → /", reason || "");
      try {
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("joined");
        localStorage.removeItem("callPageLeaveFlag");
      } catch (_) {
        /* ignore */
      }
      setIsAuth(false);
      navigate("/", { replace: true });
    },
    [navigate],
  );

  const resolveMode = (data) => {
    if (!data) return "";
    return (
      data.mode ||
      data?.service?.mode?.slug ||
      data?.callerData?.mode ||
      ""
    );
  };

  const mergeAppointmentCredentials = useCallback(
    (fromProfile, existing) => {
      const prev =
        existing ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("appointmentData") || "{}") || {};
          } catch {
            return {};
          }
        })();

      // Prefer URL id as source of truth for THIS page
      const appointmentId =
        urlAppointmentId ||
        (fromProfile?._id ? String(fromProfile._id) : "") ||
        prev.appointmentId ||
        "";

      const sessionId = fromProfile?.sessionId || prev.sessionId || null;
      const tokenVal =
        fromProfile?.sessionToken || fromProfile?.token || prev.token || null;
      const mode =
        fromProfile?.service?.mode?.slug ||
        prev.mode ||
        resolveMode(prev) ||
        "";

      return {
        ...prev,
        appointmentId,
        appointmentStartTime:
          fromProfile?.consultantJoinTime ||
          fromProfile?.sessionStartTime ||
          prev.appointmentStartTime ||
          null,
        activeArray: fromProfile?.activeArray || prev.activeArray,
        waitingArray: fromProfile?.waitingArray || prev.waitingArray,
        callerData: {
          consultant: {
            id: fromProfile?.consultantId || prev?.callerData?.consultant?.id,
            image:
              fromProfile?.consultantDetails?.image ||
              prev?.callerData?.consultant?.image,
            name: fromProfile?.consultantDetails
              ? `${fromProfile.consultantDetails.firstName || ""} ${fromProfile.consultantDetails.lastName || ""}`.trim()
              : prev?.callerData?.consultant?.name,
          },
          user: {
            id: fromProfile?.clientId || prev?.callerData?.user?.id,
            image:
              fromProfile?.clientDetails?.image || prev?.callerData?.user?.image,
            name: fromProfile?.clientDetails
              ? `${fromProfile.clientDetails.firstName || ""} ${fromProfile.clientDetails.lastName || ""}`.trim()
              : prev?.callerData?.user?.name,
          },
          mode: mode || prev?.callerData?.mode,
        },
        consultantId: fromProfile?.consultantId || prev.consultantId,
        clientId: fromProfile?.clientId || prev.clientId,
        mode,
        paymentMode: fromProfile?.paymentMode || prev.paymentMode,
        sessionId,
        token: tokenVal,
        service:
          prev.service ||
          fromProfile?.service?.service?.slug ||
          "direct-access-online-psychological-counseling",
      };
    },
    [urlAppointmentId],
  );

  const authenticateAppointment = useCallback(async () => {
    const appointmentId = urlAppointmentId || appointmentData?.appointmentId;
    if (!appointmentId) {
      // Allow paint if we only have local chat payload (ring just accepted)
      const local = (() => {
        try {
          return JSON.parse(localStorage.getItem("appointmentData") || "{}");
        } catch {
          return {};
        }
      })();
      if (local?.appointmentId || local?.mode === "chat") {
        setIsAuth(true);
        return;
      }
      navigateHome("no-appointment-id");
      return;
    }

    try {
      const res = await apiRequest("post", "appointment/authenticate-appointment", {
        body: { appointmentId },
      });

      const apptStatus =
        res?.data?.data?.appointmentStatus || res?.data?.data?.status;
      // HTTP errors only — status field can be "close" with HTTP 200
      if ([401, 403, 404].includes(res?.status) || [401, 403, 404].includes(res?.data?.status)) {
        localStorage.removeItem("joined");
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("callPageLeaveFlag");
        toast.info("This consultation has ended.", { autoClose: 3000 });
        navigateHome("auth-http-denied");
        return;
      }
      if (apptStatus === "close" || apptStatus === "cancel") {
        localStorage.removeItem("joined");
        localStorage.removeItem("appointmentData");
        localStorage.removeItem("callPageLeaveFlag");
        toast.info("This consultation has ended.", { autoClose: 3000 });
        navigateHome("auth-closed");
        return;
      }
      setIsAuth(true);
    } catch (error) {
      console.error("Error verifying appointment:", error);
      // Chat has no OpenTok token — do not require sessionId
      const local = (() => {
        try {
          return JSON.parse(localStorage.getItem("appointmentData") || "{}");
        } catch {
          return {};
        }
      })();
      const mode = resolveMode(local);
      if (
        String(local?.appointmentId || "") === String(appointmentId) ||
        mode === "chat" ||
        local?.sessionId
      ) {
        setIsAuth(true);
        return;
      }
      toast.error(error?.data?.message || "Could not verify appointment.", {
        autoClose: 4000,
      });
      navigateHome("auth-error");
    }
  }, [appointmentData?.appointmentId, urlAppointmentId, navigateHome]);

  const fetchProfileDetails = useCallback(async () => {
    try {
      const response = await getProfileDetails(token);
      const profileData = response?.data?.docs;

      if (response?.data?.status === 200 && profileData) {
        localStorage.setItem("userDetails", JSON.stringify(profileData));
        setProfile(profileData);

        const localAppt = (() => {
          try {
            return JSON.parse(localStorage.getItem("appointmentData") || "null");
          } catch {
            return null;
          }
        })();

        const profileAppt = profileData?.appointmentData;
        const profileApptId = profileAppt?._id ? String(profileAppt._id) : "";
        const localId = localAppt?.appointmentId
          ? String(localAppt.appointmentId)
          : "";
        const pageId = urlAppointmentId || localId;

        // Only merge profile appointment if it is THE same appointment as this page
        if (
          profileAppt &&
          pageId &&
          profileApptId &&
          profileApptId === pageId &&
          ["active", "inProgress"].includes(String(profileAppt.status || ""))
        ) {
          const merged = mergeAppointmentCredentials(profileAppt, localAppt);
          localStorage.setItem("appointmentData", JSON.stringify(merged));
          setAppointmentData(merged);
        } else if (localAppt?.appointmentId) {
          // Keep local ring payload (has mode/callerData) — critical for chat
          // Ensure URL id is stored
          const fixed = {
            ...localAppt,
            appointmentId: pageId || localAppt.appointmentId,
            mode: resolveMode(localAppt) || localAppt.mode || "chat",
          };
          localStorage.setItem("appointmentData", JSON.stringify(fixed));
          setAppointmentData(fixed);
        } else if (profileAppt && profileApptId === pageId) {
          const merged = mergeAppointmentCredentials(profileAppt, null);
          localStorage.setItem("appointmentData", JSON.stringify(merged));
          setAppointmentData(merged);
        }

        // ── REMOVED fatal bug ──
        // Old code: for direct-access, if !consultantJoinTime && !sessionId → home.
        // Chat never has sessionId; consultant just joining has no joinTime yet → false redirect.

        if (!localStorage.getItem("joined")) {
          localStorage.setItem("joined", "true");
        }
      } else {
        // Soft fail: if we have local chat appointment, still allow
        const local = (() => {
          try {
            return JSON.parse(localStorage.getItem("appointmentData") || "{}");
          } catch {
            return {};
          }
        })();
        if (local?.appointmentId || urlAppointmentId) {
          if (!localStorage.getItem("joined")) {
            localStorage.setItem("joined", "true");
          }
          return;
        }
        toast.error("Invalid profile details.", { autoClose: 4000 });
        navigateHome("profile-invalid");
      }
    } catch (error) {
      console.error("Error fetching profile details:", error);
      const local = (() => {
        try {
          return JSON.parse(localStorage.getItem("appointmentData") || "{}");
        } catch {
          return {};
        }
      })();
      if (local?.appointmentId || urlAppointmentId || resolveMode(local) === "chat") {
        return;
      }
      toast.error("Error fetching profile details.", { autoClose: 4000 });
      navigateHome("profile-error");
    }
  }, [token, mergeAppointmentCredentials, urlAppointmentId, navigateHome]);

  const handleVerifyAlreadyInCall = useCallback(async () => {
    const appointmentId =
      urlAppointmentId || appointmentData?.appointmentId || id;
    const profileId = profile?._id || (() => {
      try {
        return JSON.parse(localStorage.getItem("userDetails") || "{}")?._id;
      } catch {
        return null;
      }
    })();
    if (!appointmentId || !profileId) return;

    try {
      const res = await apiRequest("get", "appointment/get-already-in-call", {
        queries: { appointmentId },
      });

      const { status, data } = res?.data || {};
      const activeArray = (data?.activeArray || []).map((x) => String(x));
      if (status !== 200) {
        // Still try to join so activeArray is populated for peer wait UI
        await apiRequest("post", "appointment/already-in-call", {
          body: {
            userId: profileId,
            appointmentId,
            disconnect: "false",
            deviceId: getDeviceId(),
          },
        }).catch(() => {});
        return;
      }

      const hasJoinedFlag = localStorage.getItem("joined") === "true";
      const alreadyHere = activeArray.includes(String(profileId));
      if (alreadyHere && !hasJoinedFlag && activeArray.length >= 1) {
        // Same user re-entering after Join — allow if URL matches
        localStorage.setItem("joined", "true");
      }

      await apiRequest("post", "appointment/already-in-call", {
        body: {
          userId: profileId,
          appointmentId,
          disconnect: "false",
          deviceId: getDeviceId(),
        },
      });
    } catch (error) {
      console.error("Error verifying call status:", error);
    }
  }, [appointmentData?.appointmentId, profile?._id, id, urlAppointmentId]);

  useEffect(() => {
    if (!token) {
      navigate("/signin", { replace: true });
      return;
    }

    const boot = async () => {
      // Mark joined immediately so multi-device guard does not bounce us
      try {
        localStorage.setItem("joined", "true");
        const local = JSON.parse(localStorage.getItem("appointmentData") || "{}");
        if (urlAppointmentId && (!local.appointmentId || String(local.appointmentId) !== urlAppointmentId)) {
          localStorage.setItem(
            "appointmentData",
            JSON.stringify({
              ...local,
              appointmentId: urlAppointmentId,
              mode: local.mode || "chat",
            }),
          );
        }
      } catch (_) {
        /* ignore */
      }

      await fetchProfileDetails();
      await authenticateAppointment();
      await handleVerifyAlreadyInCall();
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, urlAppointmentId]);

  useEffect(() => {
    if (!socket) return;
    const apptId = urlAppointmentId || appointmentData?.appointmentId || id;
    if (apptId) {
      socket.emit("joinAppointmentRoom", { appointmentId: apptId });
    }
    const tick = () => {
      if (!socket.connected) return;
      socket.emit("userPing");
      if (apptId) {
        socket.emit("appointmentPing", { appointmentId: apptId, onCall: true });
      }
    };
    tick();
    const interval = setInterval(tick, 20 * 1000);
    return () => {
      clearInterval(interval);
      if (apptId) socket.emit("leaveAppointmentRoom", { appointmentId: apptId });
    };
  }, [socket, appointmentData?.appointmentId, id, urlAppointmentId]);

  // Show shell once authenticated; dark flash only while waiting
  if (!isAuth) {
    return <div className="min-h-screen bg-zinc-900" aria-hidden="true" />;
  }

  return <Outlet />;
};

export default BlankLayout;
